import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Filter } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useFunnel, type FunnelStep } from '@/hooks/useDashboardAnalytics';
import type { PeriodValue } from './PeriodSelector';
import { cn } from '@/lib/utils';

interface FunnelWidgetProps {
  period: PeriodValue;
  className?: string;
}

const STEP_COLORS = [
  'bg-blue-500',
  'bg-indigo-500',
  'bg-purple-500',
  'bg-pink-500',
] as const;

function formatCount(count: number): string {
  return new Intl.NumberFormat('ru-RU').format(count);
}

interface FunnelRowProps {
  step: FunnelStep;
  index: number;
  maxCount: number;
  previousStepCount: number | null;
}

function FunnelRow({ step, index, maxCount, previousStepCount }: FunnelRowProps) {
  const barColor = STEP_COLORS[index] ?? 'bg-muted';
  const widthPercent = maxCount === 0 ? 0 : Math.max(4, (step.count / maxCount) * 100);
  const dropoff =
    previousStepCount !== null && previousStepCount > 0
      ? ((previousStepCount - step.count) / previousStepCount) * 100
      : null;

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-2 text-sm">
              <span className="font-medium text-foreground">{step.name}</span>
              <span className="flex items-center gap-2 tabular-nums text-muted-foreground">
                <span>{formatCount(step.count)}</span>
                <span className="text-xs">•</span>
                <span className="font-medium text-foreground">{step.rate}%</span>
              </span>
            </div>
            <div className="h-8 rounded-md bg-muted/40 overflow-hidden">
              <div
                className={cn('h-full rounded-md transition-all', barColor)}
                style={{ width: `${widthPercent}%` }}
              />
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" align="center">
          <div className="space-y-0.5 text-xs">
            <p className="font-medium">{step.name}</p>
            <p>Кол-во: {formatCount(step.count)}</p>
            <p>Конверсия от старта: {step.rate}%</p>
            {dropoff !== null && (
              <p className="text-muted-foreground">
                Отсев от предыдущего шага: {dropoff.toFixed(1)}%
              </p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function FunnelWidget({ period, className }: FunnelWidgetProps) {
  const { data, isLoading } = useFunnel(period.type, period.startDate, period.endDate);

  const maxCount = useMemo(() => {
    if (!data?.steps.length) {return 0;}
    return Math.max(...data.steps.map((s) => s.count));
  }, [data?.steps]);

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Воронка конверсии
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pb-5">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-[80%]" />
          <Skeleton className="h-14 w-[55%]" />
          <Skeleton className="h-14 w-[30%]" />
        </CardContent>
      </Card>
    );
  }

  const steps = data?.steps ?? [];

  if (steps.every((s) => s.count === 0)) {
    return (
      <Card className={className}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Воронка конверсии
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-8">
            Нет данных за выбранный период
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Filter className="h-4 w-4" />
          Воронка конверсии
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          От посетителя до повторной покупки
        </p>
      </CardHeader>
      <CardContent className="space-y-4 pb-5">
        {steps.map((step, i) => (
          <FunnelRow
            key={step.name}
            step={step}
            index={i}
            maxCount={maxCount}
            previousStepCount={i === 0 ? null : (steps[i - 1]?.count ?? null)}
          />
        ))}
      </CardContent>
    </Card>
  );
}
